import React, { useEffect, useMemo } from 'react'
import { useAppSelector } from '../store/hooks'
import { selectAuthenticatedUser, selectAuthenticatedUserToken } from '../store/auth/authSlice'
import { useGetBillingCustomerQuery } from '../store/plan/plan'
import { Auth } from '@zocks-communications/client-sdk-js'
import { ErrorBoundary } from 'react-error-boundary'

const ensureHotjarInitialized = (hjid: number) => {
  const w = window as unknown as {
    hj?: unknown
    _hjSettings?: { hjid: number; hjsv: number }
    __hotjarInitialized?: boolean
    __hotjarSiteId?: number
  }

  const existing = document.querySelector('script[data-hotjar-script="true"]') as HTMLScriptElement | null
  if (existing) {
    w.__hotjarInitialized = true
  }
  if (w.__hotjarInitialized || w.hj) {
    return
  }
  ;(w as unknown as { hj: (...args: unknown[]) => void }).hj = function (...args: unknown[]) {
    const fn = (w as unknown as { hj: { q?: unknown[][] } }).hj
    fn.q = fn.q || []
    fn.q.push(args)
  }

  w._hjSettings = { hjid, hjsv: 6 }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://static.hotjar.com/c/hotjar-${hjid}.js?sv=${w._hjSettings.hjsv}`
  script.dataset.hotjarScript = 'true'
  document.head.appendChild(script)

  w.__hotjarInitialized = true
  w.__hotjarSiteId = hjid
}

const parseCommaSeparated = (value: string | undefined) => {
  if (!value) {
    return [] as string[]
  }
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
}

const HotjarBootstrapInner: React.FC = () => {
  const user = useAppSelector(selectAuthenticatedUser)
  const userToken = useAppSelector(selectAuthenticatedUserToken)

  const userCanQueryBilling = useMemo(() => {
    if (!user || !userToken?.accessToken) {
      return false
    }
    const zocksAuth = new Auth({ accountId: user.accountId, apiToken: userToken.accessToken })
    return zocksAuth.hasScope('**', 'billing_customer:read', ['billing_customer:admin'])
  }, [user, userToken])

  const accountId = user?.accountId
  const { data: billingCustomer } = useGetBillingCustomerQuery({ accountId: accountId! }, { skip: !accountId || !userCanQueryBilling })
  const planId = billingCustomer?.data?.subscription?.planId

  useEffect(() => {
    try {
      if (!planId) {
        return
      }

      const allowedPlanIds = parseCommaSeparated(import.meta.env.REACT_APP_HOTJAR_ALLOWED_PLAN_IDS)
      const siteId = import.meta.env.REACT_APP_HOTJAR_SITE_ID

      if (!allowedPlanIds.includes(planId) || !siteId) {
        return
      }

      const hjid = Number(siteId)
      if (!Number.isFinite(hjid)) {
        return
      }

      ensureHotjarInitialized(hjid)
    } catch (error) {
      console.error(error)
    }
  }, [planId])

  return null
}

export const HotjarBootstrap: React.FC = () => {
  return (
    <ErrorBoundary fallback={null}>
      <HotjarBootstrapInner />
    </ErrorBoundary>
  )
}
